import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';

import {
  RESUME_ID_QUERY,
  SEED_QUERY,
  TEMPLATE_GROUP_QUERY,
  TEMPLATE_QUERY,
} from '@glass/common/modules/pages/queryParams';
import createSlug from '@glass/common/modules/url/createSlug';
import SpinnerWithContainer from '@glass/web/components/Loading/SpinnerWithContainer';
import Tab from '@glass/web/components/base/Tab';
import Tabs from '@glass/web/components/base/Tabs';
import Grid from '@glass/web/components/base/Unstable_Grid2';
import useQueryParamState from '@glass/web/modules/router/useQueryParamState';
import makeStyles from '@glass/web/modules/theme/makeStyles';

import ResumeTemplate from '@glass/shared/components/ResumeBuilder/TemplateChooser/ResumeTemplate';
import useResumeWords from '@glass/shared/modules/content/staticVariations/resumeWords/useResumeWords';
import usePageTerms from '@glass/shared/modules/content/staticVariations/usePageTerms';
import useContentSeed from '@glass/shared/modules/content/useContentSeed';
import useTemplateMetas from '@glass/shared/modules/resumes/hooks/useTemplateMetas';

const useStyles = makeStyles()((theme) => ({
  enter: {
    opacity: 0,
  },
  enterActive: {
    opacity: 1,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.enteringScreen,
    }),
  },
  exit: {
    opacity: 1,
  },
  exitActive: {
    opacity: 0,
    transition: theme.transitions.create('opacity', {
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  tabs: {
    justifyContent: 'center',
  },
  scroller: {
    flexGrow: 0,
  },
}));

const ALL_LABEL = 'All';
const RECOMMENDED_LABEL = 'Recommended';
const TRADITIONAL_LABEL = 'Traditional';
const MODERN_LABEL = 'Modern';
const ARTISTIC_LABEL = 'Artistic';

const createTabGroupId = (label) => createSlug(label);
const createAria = (label) => createSlug(`${label}-templates`);

const RECOMMENDED_GROUP_ID = createTabGroupId(RECOMMENDED_LABEL);
const ALL_GROUP_ID = createTabGroupId(ALL_LABEL);

const TABS = [
  {
    label: ALL_LABEL,
  },
  {
    label: RECOMMENDED_LABEL,
  },
  {
    label: MODERN_LABEL,
  },
  {
    label: TRADITIONAL_LABEL,
  },
  {
    label: ARTISTIC_LABEL,
  },
].map((data, idx) => ({
  ...data,
  id: createTabGroupId(data.label),
  aria: createAria(data.label),
  idx,
}));

const isValidTabId = (tabId) => tabId && TABS.some(({ id }) => tabId === id);

function TabTemplateChooser({ contentResumeSlug, loading: loadingProp }) {
  const {
    isReady,
    query: {
      [TEMPLATE_QUERY]: templateQuery,
      [TEMPLATE_GROUP_QUERY]: templateGroupIdQuery,
      [SEED_QUERY]: seedOverride,
      [RESUME_ID_QUERY]: resumeId,
    },
  } = useRouter();

  const { iSlug, jSlug } = usePageTerms();
  const seed = useContentSeed({ seedOverride, iSlug, jSlug });

  const { templateMetas, loading } = useTemplateMetas({
    seedOverride,
    jSlug,
    iSlug,
  });

  const [templateGroupId, setTemplateGroupId] = useState(() =>
    isValidTabId(templateGroupIdQuery) ? templateGroupIdQuery : RECOMMENDED_GROUP_ID,
  );

  useEffect(() => {
    if (isReady && templateMetas?.length) {
      if (templateQuery && templateMetas.some(({ slug }) => slug === templateQuery)) {
        setTemplateGroupId(ALL_GROUP_ID);
      } else if (isValidTabId(templateGroupIdQuery) && templateGroupId !== templateGroupIdQuery) {
        setTemplateGroupId(templateGroupIdQuery);
      }
    }
    // this is in case the group cis set from the query param
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady, templateMetas]);

  const { classes } = useStyles();

  const handleTabChange = useCallback((event, selectedTabId) => {
    setTemplateGroupId(selectedTabId);
  }, []);

  const resumeWords = useResumeWords();

  const templates = useMemo(() => {
    const selectedTab = TABS.find(({ id }) => id === templateGroupId);
    let result;
    if (selectedTab.label === ALL_LABEL) {
      result = templateMetas;
    } else if (selectedTab.idx === TABS.length - 1) {
      result = templateMetas?.slice((selectedTab.idx - 1) * 3);
    } else {
      result = templateMetas?.slice((selectedTab.idx - 1) * 3, selectedTab.idx * 3);
    }

    if (templateQuery) {
      const selectedIndex = result?.findIndex(({ slug }) => slug === templateQuery);
      if (selectedIndex > -1) {
        return [
          result[selectedIndex],
          ...result.slice(0, selectedIndex),
          ...result.slice(selectedIndex + 1),
        ];
      }
    }

    return result;
  }, [templateQuery, templateGroupId, templateMetas]);

  useQueryParamState(TEMPLATE_GROUP_QUERY, templateGroupId, {
    queryValueOverrides: { [RECOMMENDED_GROUP_ID]: '' },
  });

  const addEndListener = useCallback((node, done) => {
    node.addEventListener('transitionend', done, false);
  }, []);

  const transitionClassnames = useMemo(
    () => ({
      enter: classes.enter,
      enterActive: classes.enterActive,
      exit: classes.exit,
      exitActive: classes.exitActive,
    }),
    [classes],
  );

  if (loading || loadingProp) {
    return <SpinnerWithContainer />;
  }

  if (!templates?.length) {
    return null;
  }

  return (
    <Grid container spacing={1}>
      <Grid xs={12}>
        <Tabs
          aria-label={`${resumeWords.capitalized} templates`}
          classes={{ root: classes.tabs, scroller: classes.scroller }}
          indicatorColor="primary"
          scrollButtons="auto"
          textColor="primary"
          value={templateGroupId}
          variant="scrollable"
          onChange={handleTabChange}
        >
          {TABS.map(({ label, id, aria }) => (
            <Tab key={id} aria-controls={aria} label={label} value={id} />
          ))}
        </Tabs>
      </Grid>
      <Grid xs={12}>
        <SwitchTransition mode="out-in">
          <CSSTransition
            key={templateGroupId}
            addEndListener={addEndListener}
            classNames={transitionClassnames}
          >
            <Grid container spacing={1}>
              {templates?.map(({ name, slug, subheader, updatedAt }, idx) => {
                return (
                  <Grid key={slug} md={4} sm={6} xs={12}>
                    <ResumeTemplate
                      contentResumeSlug={contentResumeSlug}
                      idx={idx}
                      load={!loading}
                      name={name}
                      resumeId={resumeId}
                      seed={seed}
                      selected={slug === templateQuery}
                      slug={slug}
                      subheader={subheader}
                      updatedAt={updatedAt}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </CSSTransition>
        </SwitchTransition>
      </Grid>
    </Grid>
  );
}

TabTemplateChooser.propTypes = {
  contentResumeSlug: PropTypes.string,
  loading: PropTypes.bool,
};

export default React.memo(TabTemplateChooser);
